import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Layout from '@/components/Layout';
import PollenDotsNav from '@/components/PollenDotsNav';
import { API, useAuth } from '@/App';
import { Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Hexagon, Plus, Users, CreditCard, Receipt, Building2, ChevronLeft, ChevronRight,
  MapPin, Bed, Bus, Compass, CalendarDays, TrendingUp, IndianRupee, Loader2, ArrowRight,
  Repeat, UserPlus, Trophy, Crown, Medal, Eye, EyeOff, AlertTriangle, Calendar, Pencil, Check, X, RefreshCw, CheckCircle2, Sparkles, MessageCircle, ChevronDown, ChevronUp,
} from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths, isSameDay, parseISO } from 'date-fns';
import { formatDate } from '@/lib/dateUtils';
import { usePollenCapabilities } from '@/hooks/usePollenCapabilities';
import { useShowProfits } from '@/hooks/useShowProfits';
import PollenConnectionsMap from '@/components/PollenConnectionsMap';
import { CelebrationsWidget } from '@/components/Celebrations';
import { AlertsRibbon } from '@/components/Alerts';
import VendorDataGapsCard from '@/components/VendorDataGapsCard';
import ShareTrackingCard from '@/components/ShareTrackingCard';
import OpsScheduleCard from '@/components/OpsScheduleCard';

const eventColors = {
  hotel_checkin: 'bg-emerald-500',
  hotel_checkout: 'bg-red-500',
  activity: 'bg-blue-500',
  transport: 'bg-amber-500',
};
const eventLabels = {
  hotel_checkin: 'Check-in',
  hotel_checkout: 'Check-out',
  activity: 'Activity',
  transport: 'Transport',
};

const PollenDotsDashboard = () => {
  const POLLEN_API = `${API}/pollen`;
  const { user } = useAuth();
  const navigate = useNavigate();
  const caps = usePollenCapabilities();
  const [showProfits, , toggleProfits] = useShowProfits();
  // Back-compat alias for existing logic
  const showReferralProfits = showProfits;
  const setShowReferralProfits = toggleProfits;

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarEvents, setCalendarEvents] = useState({});
  const [nextMonthEvents, setNextMonthEvents] = useState({});
  const [dateDetails, setDateDetails] = useState([]);
  const [kpis, setKpis] = useState({ total_tours: 0, total_days: 0, total_travellers: 0, total_revenue: 0 });
  const [kpiPeriod, setKpiPeriod] = useState('lifetime');
  // CAC settings (operator-tunable). Pollen Dots' real-world CAC is ₹0 because
  // the business runs entirely on word-of-mouth + referrals — when paid
  // marketing kicks in, the operator can edit the values from the card itself.
  const [cacEditOpen, setCacEditOpen] = useState(false);
  const [cacForm, setCacForm] = useState({ cac_direct: 0, cac_referred: 0 });
  const [cacBusy, setCacBusy] = useState(false);
  const [payables, setPayables] = useState([]);
  const [payableFilter, setPayableFilter] = useState('all');
  const [performance, setPerformance] = useState({ total_revenue: 0, total_cost: 0, gross_profit: 0, graph_data: [] });
  const [loading, setLoading] = useState(true);
  const [modalType, setModalType] = useState(null);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [tours, setTours] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [leaderboards, setLeaderboards] = useState({ top_team: [], top_customers: [] });
  const [lbMetric, setLbMetric] = useState('revenue');

  // Force "By Revenue" leaderboard when profit toggle is off
  useEffect(() => {
    if ((!caps.is_super_admin || !showReferralProfits) && lbMetric === 'profit') setLbMetric('revenue');
  }, [caps.is_super_admin, showReferralProfits, lbMetric]);

  const authHeaders = () => {
    const h = {};
    const t = localStorage.getItem('session_token');
    if (t) h['Authorization'] = `Bearer ${t}`;
    return h;
  };
  const authFetch = (url, opts = {}) => fetch(url, { ...opts, credentials: 'include', headers: { ...opts.headers, ...authHeaders() } });

  const fetchAll = useCallback(async () => {
    const monthStr = format(currentMonth, 'yyyy-MM');
    const nextMonthStr = format(addMonths(currentMonth, 1), 'yyyy-MM');
    try {
      const [calRes, nextCalRes, kpiRes, payRes, perfRes, tourRes, vendorRes, lbRes] = await Promise.all([
        authFetch(`${POLLEN_API}/dashboard/calendar?month=${monthStr}`),
        authFetch(`${POLLEN_API}/dashboard/calendar?month=${nextMonthStr}`),
        authFetch(`${POLLEN_API}/dashboard/kpis?period=${kpiPeriod}`),
        authFetch(`${POLLEN_API}/dashboard/vendor-payables?filter=${payableFilter}`),
        authFetch(`${POLLEN_API}/dashboard/performance`),
        authFetch(`${POLLEN_API}/tours`),
        authFetch(`${POLLEN_API}/vendors`),
        authFetch(`${POLLEN_API}/dashboard/leaderboards?period=${kpiPeriod}&metric=${lbMetric}`),
      ]);
      if (calRes.ok) { const d = await calRes.json(); setCalendarEvents(d.events || {}); }
      if (nextCalRes.ok) { const d = await nextCalRes.json(); setNextMonthEvents(d.events || {}); }
      if (kpiRes.ok) setKpis(await kpiRes.json());
      if (payRes.ok) setPayables(await payRes.json());
      if (perfRes.ok) setPerformance(await perfRes.json());
      if (tourRes.ok) setTours(await tourRes.json());
      if (vendorRes.ok) setVendors(await vendorRes.json());
      if (lbRes.ok) setLeaderboards(await lbRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [currentMonth, kpiPeriod, payableFilter, lbMetric]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleDateClick = async (dateStr) => {
    setSelectedDate(dateStr);
    try {
      const res = await authFetch(`${POLLEN_API}/dashboard/date-details?date=${dateStr}`);
      if (res.ok) setDateDetails(await res.json());
    } catch (e) { console.error(e); }
  };

  const handleQuickAdd = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      let url = '', payload = {};
      if (modalType === 'customer') {
        url = `${POLLEN_API}/customers`;
        payload = { name: formData.name || '', phone: formData.phone || '', email: formData.email || '', address: formData.address || '', zoho_customer_id: formData.zoho_customer_id || '' };
      } else if (modalType === 'vendor') {
        url = `${POLLEN_API}/vendors`;
        payload = { name: formData.name || '', type: formData.type || 'Hotel', contact_name: formData.contact_name || '', phone: formData.phone || '', email: formData.email || '' };
      } else if (modalType === 'payment') {
        url = `${POLLEN_API}/payments`;
        payload = { tour_id: formData.tour_id || '', vendor_name: formData.vendor_name || '', category: formData.category || '', amount: parseFloat(formData.amount) || 0, date: formData.date || '', notes: formData.notes || '' };
      } else if (modalType === 'expense') {
        url = `${POLLEN_API}/expenses`;
        payload = { tour_id: formData.tour_id || '', category: formData.category || '', description: formData.description || '', amount: parseFloat(formData.amount) || 0, date: formData.date || '' };
      }
      const res = await authFetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (res.ok) { toast.success(`${modalType} added successfully`); setModalType(null); setFormData({}); fetchAll(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch (e) { toast.error('Error adding item'); }
    setSubmitting(false);
  };

  const [driftCount, setDriftCount] = useState(null);

  const fetchDriftStatus = useCallback(async () => {
    if (!(caps.is_super_admin || user?.role === 'Admin')) return;
    try {
      const r = await authFetch(`${POLLEN_API}/maintenance/drift-status`);
      if (r.ok) { const d = await r.json(); setDriftCount(d.drifted_tours); }
    } catch { /* silent */ }
  }, [caps, user]);

  useEffect(() => { fetchDriftStatus(); }, [fetchDriftStatus]);

  // Load CAC settings on mount so the edit dialog opens with the current values.
  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch(`${POLLEN_API}/dashboard/cac-settings`);
        if (res.ok) setCacForm(await res.json());
      } catch { /* silent */ }
    })();
  }, [authFetch, POLLEN_API]);

  const saveCacSettings = async () => {
    setCacBusy(true);
    try {
      const res = await authFetch(`${POLLEN_API}/dashboard/cac-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cac_direct: parseFloat(cacForm.cac_direct) || 0,
          cac_referred: parseFloat(cacForm.cac_referred) || 0,
        }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || 'Save failed');
      }
      toast.success('CAC settings saved');
      setCacEditOpen(false);
      fetchAll();
    } catch (e) { toast.error(e.message); }
    finally { setCacBusy(false); }
  };

  const recomputeAll = async () => {
    if (!confirm('Recompute every derived field across all tours (names, days, costs, balances, customer name resync)? Safe to run.')) return;
    try {
      const res = await authFetch(`${POLLEN_API}/maintenance/recompute-all`, { method: 'POST' });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || 'Recompute failed');
      }
      const d = await res.json();
      const total = Object.values(d).reduce((a, b) => a + b, 0);
      if (total === 0) toast.success('Everything is already in sync.');
      else toast.success(`Repaired: ${d.tour_names_repaired} names, ${d.tour_days_repaired} days, ${d.tour_costs_repaired} costs, ${d.hotel_balances_repaired} balances, ${d.customer_names_resynced} customer-name resyncs, ${d.geocoded_coords_stripped || 0} geocoded coords purged.`);
      fetchAll();
      fetchDriftStatus();
    } catch (e) { toast.error(e.message); }
  };

  const POLLEN_USER_ROLES = ['Pollen Dots User', 'Super Admin', 'Admin', 'Operator', 'Intern'];
  if (!POLLEN_USER_ROLES.includes(user?.role)) return <Navigate to="/dashboard" replace />;
  if (loading) return <Layout><div className="flex items-center justify-center h-[60vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></Layout>;

  return (
    <Layout>
      <div className="space-y-6" data-testid="pollen-dots-dashboard">
        <PollenDotsNav />
        {/* Operational alerts ribbon — always at the very top so the team
            can't miss things that need doing today (driver shares, hotel
            check-ins, missing tour data, vendor payments due). */}
        <AlertsRibbon />
        {/* Vendor data gap finder — compact 4-tile widget exposing fields
            that need filling. Tapping a tile opens an inline editor so
            operators can clear gaps without navigating. */}
        <VendorDataGapsCard />
        <OpsScheduleCard />
        <ShareTrackingCard />
        {/* Header + Action Buttons */}
        <div className="flex flex-col gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">Pollen Dots Digital Passport</h1>
            <p className="text-muted-foreground mt-1">Travel Operations Command Center</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => navigate('/pollen-dots/queries/new')}
              className="h-12 px-5 text-base bg-amber-600 hover:bg-amber-700 text-white shadow-md"
              data-testid="btn-log-query"
            >
              <MessageCircle className="w-5 h-5 mr-2" />Log Query
            </Button>
            <Button onClick={() => navigate('/pollen-dots/add-tour')} data-testid="btn-add-tour" variant="outline" className="h-12 px-5"><Plus className="w-4 h-4 mr-1" />Add Tour</Button>
            {(caps.is_super_admin || user?.role === 'Admin') && (
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={recomputeAll} data-testid="btn-recompute-all" title="Recompute all auto-derived fields across the system (names, days, costs, balances, customer-name resync)">
                  <RefreshCw className="w-4 h-4 mr-1" />Recompute all
                </Button>
                {driftCount !== null && (
                  driftCount === 0 ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-[11px] text-emerald-800" data-testid="drift-status-clean">
                      <CheckCircle2 className="w-3 h-3" />All in sync
                    </span>
                  ) : (
                    <button onClick={recomputeAll} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-50 border border-amber-300 text-[11px] text-amber-900 hover:bg-amber-100 transition" data-testid="drift-status-pending" title="Click to repair">
                      <AlertTriangle className="w-3 h-3" />{driftCount} record{driftCount === 1 ? '' : 's'} need recompute
                    </button>
                  )
                )}
              </div>
            )}
            {caps.is_super_admin && (
              <Button
                variant={showProfits ? 'default' : 'outline'}
                onClick={toggleProfits}
                data-testid="toggle-profit-visibility"
                title={showProfits ? 'Hide all profits platform-wide' : 'Show all profits platform-wide'}>
                {showProfits ? <Eye className="w-4 h-4 mr-1" /> : <EyeOff className="w-4 h-4 mr-1" />}
                Profit
              </Button>
            )}
            {/* Geocoder button removed — auto-geocoding pollutes locations
                with fictitious coords. All pin updates must come from a
                human-pasted Google Maps URL via the Locations admin tab. */}
          </div>
        </div>

        {/* Calendars */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <CalendarGrid month={currentMonth} events={calendarEvents} selectedDate={selectedDate}
              onDateClick={handleDateClick} onPrev={() => setCurrentMonth(subMonths(currentMonth, 1))} onNext={() => setCurrentMonth(addMonths(currentMonth, 1))} large />
          </div>
          <div className="hidden lg:block">
            <CalendarGrid month={addMonths(currentMonth, 1)} events={nextMonthEvents} selectedDate={null} onDateClick={() => {}} small />
          </div>
        </div>

        {/* Date Details */}
        {selectedDate && (
          <Card data-testid="date-details-panel">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <CalendarDays className="w-4 h-4" />Events on {selectedDate}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dateDetails.length === 0 ? <p className="text-sm text-muted-foreground">No events on this date</p> : (
                <div className="space-y-3">
                  {dateDetails.map((d, i) => (
                    <div key={i} className="p-3 rounded-lg bg-muted/50 space-y-2 cursor-pointer hover:bg-muted/80 transition-colors" onClick={() => navigate(`/pollen-dots/tour/${d.tour_id}`)}>
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm text-primary underline-offset-2 hover:underline">{d.tour_name}</span>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">{d.status}</Badge>
                          <ArrowRight className="w-3 h-3 text-muted-foreground" />
                        </div>
                      </div>
                      {d.hotels?.map((h, j) => <div key={j} className="flex items-center gap-2 text-xs text-muted-foreground"><Bed className="w-3 h-3 text-emerald-600" />{h.hotel_name} - {h.room_type || 'Room'}</div>)}
                      {d.activities?.map((a, j) => <div key={j} className="flex items-center gap-2 text-xs text-muted-foreground"><Compass className="w-3 h-3 text-blue-600" />{a.activity_name} {a.time_slot && `(${a.time_slot})`}</div>)}
                      {d.transport?.map((t, j) => <div key={j} className="flex items-center gap-2 text-xs text-muted-foreground"><Bus className="w-3 h-3 text-amber-600" />{t.vehicle_type} - {t.route}</div>)}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* KPI Counters */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold">Key Metrics</h2>
            <Tabs value={kpiPeriod} onValueChange={setKpiPeriod}>
              <TabsList className="h-8">
                <TabsTrigger value="current_year" className="text-xs px-2 h-6">This Year</TabsTrigger>
                <TabsTrigger value="previous_year" className="text-xs px-2 h-6">Last Year</TabsTrigger>
                <TabsTrigger value="lifetime" className="text-xs px-2 h-6">Lifetime</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-7 gap-3" data-testid="kpi-section">
            <KPICard icon={MapPin} label="Tours" value={kpis.total_tours} color="text-blue-600" />
            <KPICard icon={CalendarDays} label="Days" value={kpis.total_days} color="text-purple-600" />
            <KPICard icon={Users} label="Customers" value={kpis.unique_customers || 0} color="text-indigo-600" />
            <KPICard icon={Users} label="Travellers" value={kpis.total_travellers} color="text-emerald-600" />
            <KPICard icon={IndianRupee} label="Revenue" value={`${((kpis.total_revenue || 0) / 100000).toFixed(1)}L`} color="text-amber-600" sub={caps.can_view_pnl && kpis.pnl_revenue > 0 ? `P&L: ${((kpis.pnl_revenue || 0) / 100000).toFixed(1)}L + Tours: ${((kpis.tour_revenue || 0) / 100000).toFixed(1)}L` : undefined} />
            <KPICard icon={Repeat} label="Repeat Customers" value={`${kpis.repeat_rate || 0}%`} color="text-rose-600" sub={`${kpis.repeat_customers || 0} of ${kpis.unique_customers || 0} customers`} />
            <KPICard icon={Repeat} label="Repeat Tours" value={`${kpis.repeat_tour_rate || 0}%`} color="text-fuchsia-600" sub={`${kpis.repeat_tours || 0} of ${kpis.total_tours || 0} tours`} />
            <KPICard icon={UserPlus} label="Referral Revenue" value={`${kpis.referral_revenue_pct || 0}%`} color="text-teal-600" sub={`${kpis.referral_customers_count || 0} of ${kpis.unique_customers || 0} customers`} />
          </div>

          {/* Unit Economics — investor-grade CAC / LTV. Visible to anyone who
              can view profits (Super Admin, Admin). The numbers are computed
              server-side from a default acquisition-cost assumption — see the
              `unit_economics` block in /api/pollen/dashboard/kpis. */}
          {caps.can_view_profits && kpis.unit_economics && (
            <div className="mt-3 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="unit-economics-section">
              <KPICard icon={IndianRupee} label="Avg LTV" color="text-violet-600"
                value={`\u20b9${((kpis.unit_economics.avg_ltv || 0) / 100000).toFixed(2)}L`}
                sub={`per customer \u00b7 ${kpis.unique_customers || 0} customers`} />
              <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => caps.is_super_admin && setCacEditOpen(true)} data-testid="cac-card">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-muted-foreground flex items-center gap-1">Blended CAC {caps.is_super_admin && <Pencil className="w-3 h-3 opacity-40" />}</p>
                      <p className="text-2xl font-bold text-orange-600 mt-1">
                        {(kpis.unit_economics.blended_cac || 0) > 0
                          ? `\u20b9${(kpis.unit_economics.blended_cac || 0).toLocaleString('en-IN')}`
                          : '\u20b90'}
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        {(kpis.unit_economics.blended_cac || 0) > 0
                          ? `${kpis.unit_economics.direct_customers || 0} direct \u00b7 ${kpis.unit_economics.referred_customers || 0} referred`
                          : '100% organic / referral driven'}
                      </p>
                    </div>
                    <div className="rounded-lg bg-orange-50 p-2 text-orange-600"><IndianRupee className="w-4 h-4" /></div>
                  </div>
                </CardContent>
              </Card>
              <KPICard icon={Sparkles} label="LTV : CAC" color="text-emerald-600"
                value={(kpis.unit_economics.blended_cac || 0) > 0 ? `${kpis.unit_economics.ltv_to_cac || 0}x` : 'Organic'}
                sub={(kpis.unit_economics.blended_cac || 0) === 0
                      ? 'No paid spend \u00b7 100% word-of-mouth'
                      : (kpis.unit_economics.ltv_to_cac || 0) >= 10 ? 'Elite \u00b7 PMF proven'
                      : (kpis.unit_economics.ltv_to_cac || 0) >= 3 ? 'Healthy' : 'Watch'} />
              <KPICard icon={IndianRupee} label="Avg Ticket Size" color="text-blue-600"
                value={`\u20b9${((kpis.unit_economics.avg_ticket || 0) / 100000).toFixed(2)}L`}
                sub={`per tour \u00b7 ${kpis.total_tours || 0} tours`} />
            </div>
          )}
        </div>

        {/* Leaderboards — Top Referrers */}
        <div data-testid="leaderboards-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-600" />Top Referrers {kpiPeriod === 'current_year' ? '(This Year)' : kpiPeriod === 'previous_year' ? '(Last Year)' : '(Lifetime)'}</h2>
            <Tabs value={lbMetric} onValueChange={setLbMetric}>
              <TabsList className="h-8">
                <TabsTrigger value="revenue" className="text-xs px-2 h-6" data-testid="lb-metric-revenue">By Revenue</TabsTrigger>
                {caps.is_super_admin && showReferralProfits && <TabsTrigger value="profit" className="text-xs px-2 h-6" data-testid="lb-metric-profit">By Profit</TabsTrigger>}
              </TabsList>
            </Tabs>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <LeaderboardCard title="Top Team Members" items={leaderboards.top_team} metric={lbMetric}
              emptyHint="No team referrals yet. Add team members in the Team tab." onClick={(id) => navigate(`/pollen-dots/team/${id}`)} testId="lb-team" />
            <LeaderboardCard title="Top Customer Referrers" items={leaderboards.top_customers} metric={lbMetric}
              emptyHint="No customer-to-customer referrals yet." onClick={(id) => navigate(`/pollen-dots/customer/${id}`)} testId="lb-customers" />
          </div>
        </div>

        {/* Celebrations — upcoming birthdays / anniversaries with one-tap WhatsApp send */}
        <CelebrationsWidget />

        {/* Vendor Payables — grouped by urgency with inline due-date editing */}
        <VendorPayablesSection
          payables={payables}
          filter={payableFilter}
          setFilter={setPayableFilter}
          onUpdate={async (payload) => {
            try {
              const res = await authFetch(`${POLLEN_API}/dashboard/payable-due-date`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
              });
              if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                toast.error(err.detail || 'Could not save due date');
                return false;
              }
              toast.success(payload.due_date ? 'Due date set' : 'Due date cleared');
              // Re-fetch payables inline
              const r2 = await authFetch(`${POLLEN_API}/dashboard/vendor-payables?filter=${payableFilter}`);
              if (r2.ok) setPayables(await r2.json());
              return true;
            } catch { toast.error('Network error'); return false; }
          }}
          onOpenTour={(tourId) => navigate(`/pollen-dots/tour/${tourId}`)}
        />

        {/* Performance */}
        <div className={`grid grid-cols-1 ${caps.is_super_admin && !showProfits ? 'lg:grid-cols-2' : 'lg:grid-cols-3'} gap-4`}>
          <Card data-testid="perf-revenue"><CardContent className="pt-6">
            <p className="text-xs text-muted-foreground mb-1">Total Revenue (This Year)</p>
            <p className="text-2xl font-bold text-foreground">{performance.total_revenue?.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}</p>
          </CardContent></Card>
          {caps.is_super_admin && (
            <Card data-testid="perf-cost"><CardContent className="pt-6">
              <p className="text-xs text-muted-foreground mb-1">Cost of Services (This Year)</p>
              <p className="text-2xl font-bold text-foreground">{performance.total_cost?.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}</p>
            </CardContent></Card>
          )}
          {caps.is_super_admin && showProfits && (
            <Card data-testid="perf-profit"><CardContent className="pt-6">
              <p className="text-xs text-muted-foreground mb-1">Gross Profit (This Year)</p>
              <p className={`text-2xl font-bold ${performance.gross_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{performance.gross_profit?.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}</p>
            </CardContent></Card>
          )}
        </div>

        {/* Previous Years from P&L */}
        {performance.pnl_yearly?.length > 0 && (
          <div data-testid="pnl-yearly-section">
            <h2 className="text-base font-semibold mb-3">Previous Years (from P&L Statements)</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {performance.pnl_yearly.map((y, i) => (
                <Card key={i} className="border-l-4 border-l-blue-400">
                  <CardContent className="pt-4 pb-3 px-4">
                    <p className="text-xs font-semibold text-muted-foreground mb-2">{y.year}</p>
                    <div className="space-y-1">
                      <div className="flex justify-between text-sm"><span className="text-muted-foreground">Revenue</span><span className="font-bold">{y.revenue?.toLocaleString('en-IN')}</span></div>
                      <div className="flex justify-between text-sm"><span className="text-muted-foreground">COGS</span><span className="font-medium text-red-600">{y.cogs?.toLocaleString('en-IN')}</span></div>
                      {(caps.can_view_profits && (showProfits || !caps.is_super_admin)) && (
                        <div className="flex justify-between text-sm border-t pt-1"><span className="text-muted-foreground">Gross Profit</span><span className={`font-bold ${y.gross_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{y.gross_profit?.toLocaleString('en-IN')}</span></div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Charts */}
        {performance.graph_data?.length > 0 && (
          <Card data-testid="performance-chart">
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-4 h-4" />Monthly Trends (All Years)</CardTitle></CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={performance.graph_data}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${(v/1000).toFixed(0)}K`} />
                    <Tooltip formatter={(v) => v.toLocaleString('en-IN')} />
                    <Legend />
                    <Area type="monotone" dataKey="revenue" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.1} name="Revenue" />
                    <Area type="monotone" dataKey="cost" stroke="#ef4444" fill="#ef4444" fillOpacity={0.1} name="Cost of Services" />
                    {(caps.can_view_profits && (showProfits || !caps.is_super_admin)) && (
                      <Area type="monotone" dataKey="profit" stroke="#10b981" fill="#10b981" fillOpacity={0.1} name="Gross Profit" />
                    )}
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Network of Connection — every Pollen Dot across every journey */}
        <PollenConnectionsMap />

        {/* Quick Add Modals */}
        <Dialog open={!!modalType} onOpenChange={(open) => { if (!open) { setModalType(null); setFormData({}); } }}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Add {modalType?.charAt(0).toUpperCase() + modalType?.slice(1)}</DialogTitle></DialogHeader>
            <form onSubmit={handleQuickAdd} className="space-y-3 mt-2">
              {modalType === 'customer' && <>
                <Field label="Name *" value={formData.name} onChange={v => setFormData({...formData, name: v})} required testId="qa-customer-name" />
                <Field label="Phone" value={formData.phone} onChange={v => setFormData({...formData, phone: v})} testId="qa-customer-phone" />
                <Field label="Email" value={formData.email} onChange={v => setFormData({...formData, email: v})} type="email" testId="qa-customer-email" />
                <Field label="Address" value={formData.address} onChange={v => setFormData({...formData, address: v})} testId="qa-customer-address" />
                <Field label="Zoho Customer ID" value={formData.zoho_customer_id} onChange={v => setFormData({...formData, zoho_customer_id: v})} testId="qa-customer-zoho" />
              </>}
              {modalType === 'vendor' && <>
                <Field label="Name *" value={formData.name} onChange={v => setFormData({...formData, name: v})} required testId="qa-vendor-name" />
                <div><Label>Type</Label>
                  <Select value={formData.type || 'Hotel'} onValueChange={v => setFormData({...formData, type: v})}>
                    <SelectTrigger data-testid="qa-vendor-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{['Hotel', 'Transport', 'Activity', 'Agent'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select></div>
                <Field label="Contact Name" value={formData.contact_name} onChange={v => setFormData({...formData, contact_name: v})} testId="qa-vendor-contact" />
                <Field label="Phone" value={formData.phone} onChange={v => setFormData({...formData, phone: v})} testId="qa-vendor-phone" />
              </>}
              {modalType === 'payment' && <>
                <div><Label>Tour</Label>
                  <Select value={formData.tour_id || ''} onValueChange={v => setFormData({...formData, tour_id: v})}>
                    <SelectTrigger data-testid="qa-payment-tour"><SelectValue placeholder="Select tour" /></SelectTrigger>
                    <SelectContent>{tours.map(t => <SelectItem key={t.id} value={t.id}>{t.tour_name}</SelectItem>)}</SelectContent>
                  </Select></div>
                <Field label="Vendor Name" value={formData.vendor_name} onChange={v => setFormData({...formData, vendor_name: v})} testId="qa-payment-vendor" />
                <div><Label>Category</Label>
                  <Select value={formData.category || ''} onValueChange={v => setFormData({...formData, category: v})}>
                    <SelectTrigger data-testid="qa-payment-category"><SelectValue placeholder="Category" /></SelectTrigger>
                    <SelectContent>{['Hotel', 'Transport', 'Activity', 'Agent'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select></div>
                <Field label="Amount *" value={formData.amount} onChange={v => setFormData({...formData, amount: v})} type="number" required testId="qa-payment-amount" />
                <Field label="Date *" value={formData.date} onChange={v => setFormData({...formData, date: v})} type="date" required testId="qa-payment-date" />
                <Field label="Notes" value={formData.notes} onChange={v => setFormData({...formData, notes: v})} testId="qa-payment-notes" />
              </>}
              {modalType === 'expense' && <>
                <div><Label>Tour</Label>
                  <Select value={formData.tour_id || ''} onValueChange={v => setFormData({...formData, tour_id: v})}>
                    <SelectTrigger data-testid="qa-expense-tour"><SelectValue placeholder="Select tour" /></SelectTrigger>
                    <SelectContent>{tours.map(t => <SelectItem key={t.id} value={t.id}>{t.tour_name}</SelectItem>)}</SelectContent>
                  </Select></div>
                <Field label="Category *" value={formData.category} onChange={v => setFormData({...formData, category: v})} required testId="qa-expense-category" />
                <Field label="Description" value={formData.description} onChange={v => setFormData({...formData, description: v})} testId="qa-expense-desc" />
                <Field label="Amount *" value={formData.amount} onChange={v => setFormData({...formData, amount: v})} type="number" required testId="qa-expense-amount" />
                <Field label="Date *" value={formData.date} onChange={v => setFormData({...formData, date: v})} type="date" required testId="qa-expense-date" />
              </>}
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setModalType(null)}>Cancel</Button>
                <Button type="submit" disabled={submitting} data-testid="qa-submit">
                  {submitting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Add
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* CAC Settings — Super Admin only. Pollen Dots' real CAC is ₹0
            because growth is 100% organic; this lets the operator set actual
            spend when paid acquisition kicks in. */}
        <Dialog open={cacEditOpen} onOpenChange={setCacEditOpen}>
          <DialogContent className="max-w-md" data-testid="cac-edit-dialog">
            <DialogHeader>
              <DialogTitle>Customer Acquisition Cost</DialogTitle>
              <DialogDescription>
                Average rupees spent to acquire one new customer of each type. Leave at 0 while you grow organically — set real numbers once paid marketing kicks in.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <div>
                <label className="text-sm font-medium">Direct customer (₹)</label>
                <input type="number" min="0" className="mt-1 w-full h-9 px-3 rounded-md border bg-background text-sm"
                  value={cacForm.cac_direct} onChange={e => setCacForm({ ...cacForm, cac_direct: e.target.value })}
                  data-testid="cac-direct-input" />
                <p className="text-[11px] text-muted-foreground mt-1">Lead-gen, ads, brochures — anyone you reach with paid spend.</p>
              </div>
              <div>
                <label className="text-sm font-medium">Referred customer (₹)</label>
                <input type="number" min="0" className="mt-1 w-full h-9 px-3 rounded-md border bg-background text-sm"
                  value={cacForm.cac_referred} onChange={e => setCacForm({ ...cacForm, cac_referred: e.target.value })}
                  data-testid="cac-referred-input" />
                <p className="text-[11px] text-muted-foreground mt-1">Referrer thank-you / loyalty credit cost. Usually much lower than direct.</p>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setCacEditOpen(false)} disabled={cacBusy}>Cancel</Button>
              <Button onClick={saveCacSettings} disabled={cacBusy} data-testid="cac-save-btn">
                {cacBusy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

// ---- Sub Components ----

// Beautiful urgency-grouped Vendor Payables card with inline due-date editing.
const VendorPayablesSection = ({ payables, filter, setFilter, onUpdate, onOpenTour }) => {
  const [editingKey, setEditingKey] = useState(null); // `${tour_id}:${category}:${idx}`
  const [editDate, setEditDate] = useState('');
  // Inline pay-now + show-payments state. Keyed by the same `keyFor(p)`.
  const [payRow, setPayRow] = useState(null);       // row currently in "Add payment" mode
  const [payAmount, setPayAmount] = useState('');
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));
  const [payNotes, setPayNotes] = useState('');
  const [savingPay, setSavingPay] = useState(false);
  const [showPayments, setShowPayments] = useState(null); // row whose payments are expanded
  const [paymentsList, setPaymentsList] = useState([]);   // payments for that row
  const [loadingPayments, setLoadingPayments] = useState(false);

  const fmtMoney = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  const keyFor = (p) => `${p.tour_id}:${p.category}:${p.service_index}`;
  const totalBalance = payables.reduce((s, p) => s + (p.balance || 0), 0);

  // Group by urgency bucket for a calmer visual hierarchy.
  const groups = { overdue: [], due_soon: [], this_week: [], upcoming: [], unscheduled: [] };
  for (const p of payables) {
    const g = groups[p.urgency] ? p.urgency : 'unscheduled';
    groups[g].push(p);
  }
  const buckets = [
    { key: 'overdue',     label: 'Overdue',        tint: 'border-red-500 bg-red-50 text-red-700',        icon: AlertTriangle },
    { key: 'due_soon',    label: 'Due in ≤ 2 days', tint: 'border-amber-500 bg-amber-50 text-amber-700', icon: Calendar },
    { key: 'this_week',   label: 'Due This Week',  tint: 'border-sky-500 bg-sky-50 text-sky-700',        icon: Calendar },
    { key: 'upcoming',    label: 'Upcoming',       tint: 'border-emerald-500 bg-emerald-50 text-emerald-700', icon: Calendar },
    { key: 'unscheduled', label: 'Unscheduled',    tint: 'border-slate-300 bg-slate-50 text-slate-600',  icon: Calendar },
  ];

  const urgencyLabel = (u, days) => {
    if (u === 'overdue')    return `${Math.abs(days)}d overdue`;
    if (u === 'due_soon')   return days === 0 ? 'Due today' : days === 1 ? 'Due tomorrow' : `Due in ${days}d`;
    if (u === 'this_week')  return `Due in ${days}d`;
    if (u === 'upcoming')   return `In ${days}d`;
    return 'No due date';
  };
  const urgencyPill = (u) => {
    if (u === 'overdue')    return 'bg-red-100 text-red-700 border-red-200';
    if (u === 'due_soon')   return 'bg-amber-100 text-amber-700 border-amber-200';
    if (u === 'this_week')  return 'bg-sky-100 text-sky-700 border-sky-200';
    if (u === 'upcoming')   return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    return 'bg-slate-100 text-slate-600 border-slate-200';
  };

  const saveDueDate = async (p) => {
    const ok = await onUpdate({
      tour_id: p.tour_id, category: p.category,
      service_index: p.service_index, due_date: editDate || '',
    });
    if (ok) setEditingKey(null);
  };

  const authHdrs = () => {
    const t = localStorage.getItem('session_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const startAddPay = (p) => {
    setPayRow(keyFor(p));
    setPayAmount(String(p.balance || ''));
    setPayDate(new Date().toISOString().slice(0, 10));
    setPayNotes('');
  };

  const submitPay = async (p) => {
    if (!payAmount || Number(payAmount) <= 0) {
      toast.error('Enter a valid amount'); return;
    }
    setSavingPay(true);
    try {
      const res = await fetch(`${API}/pollen/payments`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHdrs() },
        body: JSON.stringify({
          tour_id: p.tour_id, vendor_id: p.vendor_id || '', vendor_name: p.vendor_name,
          category: p.category, amount: Number(payAmount),
          date: payDate, notes: payNotes,
        }),
      });
      if (!res.ok) { toast.error('Could not save payment'); return; }
      toast.success(`Recorded ₹${Number(payAmount).toLocaleString('en-IN')} to ${p.vendor_name}`);
      setPayRow(null);
      onUpdate && onUpdate({ tour_id: p.tour_id, category: p.category, service_index: p.service_index, due_date: p.due_date || '' });
    } finally { setSavingPay(false); }
  };

  const togglePayments = async (p) => {
    const k = keyFor(p);
    if (showPayments === k) { setShowPayments(null); return; }
    setShowPayments(k);
    setLoadingPayments(true);
    try {
      const params = new URLSearchParams({
        tour_id: p.tour_id, category: p.category,
      });
      if (p.vendor_id) params.set('vendor_id', p.vendor_id);
      const res = await fetch(`${API}/pollen/payments?${params}`, { credentials: 'include', headers: authHdrs() });
      if (res.ok) setPaymentsList(await res.json());
      else setPaymentsList([]);
    } finally { setLoadingPayments(false); }
  };

  return (
    <Card data-testid="vendor-payables-section">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <IndianRupee className="w-4 h-4 text-primary" />
            <CardTitle className="text-base">Vendor Payables</CardTitle>
            <Badge variant="outline" className="text-[10px]">{payables.length}</Badge>
            {totalBalance > 0 && (
              <Badge className="bg-red-50 text-red-700 border-red-200 text-[10px]" variant="outline">
                Outstanding: {fmtMoney(totalBalance)}
              </Badge>
            )}
          </div>
          <div className="flex gap-1 flex-wrap">
            {['all', 'overdue', 'this_week', 'upcoming', 'unscheduled'].map(f => (
              <Button key={f} variant={filter === f ? 'default' : 'outline'} size="sm"
                className="text-[11px] h-7 px-2" onClick={() => setFilter(f)}
                data-testid={`payable-filter-${f}`}>
                {f === 'all' ? 'All' : f === 'this_week' ? 'This Week'
                  : f === 'unscheduled' ? 'No Date'
                  : f.charAt(0).toUpperCase() + f.slice(1)}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {payables.length === 0 ? (
          <div className="text-center py-8 text-sm text-muted-foreground">
            <Check className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
            All caught up — nothing due in this filter.
          </div>
        ) : (
          /* Bounded scroll — long payable lists stay readable and the page
             doesn't become one endless column. Height chosen to show ~6 rows. */
          <div className="space-y-4 max-h-[28rem] overflow-y-auto pr-1" data-testid="payables-scroll-container">
            {buckets.filter(b => groups[b.key].length > 0).map(b => {
              const BucketIcon = b.icon;
              return (
                <div key={b.key} data-testid={`payable-bucket-${b.key}`}>
                  <div className={`flex items-center gap-2 px-2 py-1 border-l-4 rounded-sm text-xs font-medium ${b.tint}`}>
                    <BucketIcon className="w-3.5 h-3.5" />
                    <span>{b.label}</span>
                    <Badge variant="outline" className="text-[10px] bg-white ml-auto">{groups[b.key].length}</Badge>
                  </div>
                  <div className="mt-2 space-y-1.5">
                    {groups[b.key].map((p) => {
                      const k = keyFor(p);
                      const isEditing = editingKey === k;
                      return (
                        <div key={k}
                          className="flex flex-wrap items-center gap-2 px-3 py-2 rounded-lg border bg-white hover:bg-muted/30 transition-colors"
                          data-testid={`payable-row-${k}`}>
                          {/* Left: vendor + trip */}
                          <div className="flex-1 min-w-[180px]">
                            <button type="button" onClick={() => onOpenTour(p.tour_id)}
                              className="text-sm font-medium truncate block hover:text-primary text-left">
                              {p.vendor_name}
                            </button>
                            <div className="text-[11px] text-muted-foreground truncate flex items-center gap-1">
                              <Badge variant="outline" className="text-[9px] h-4 px-1">{p.category}</Badge>
                              <span className="truncate">{p.trip_name || '—'}</span>
                            </div>
                          </div>
                          {/* Middle: amounts (compact, progress bar) */}
                          <div className="w-[180px]">
                            <div className="flex justify-between text-[11px] text-muted-foreground mb-0.5">
                              <span>Paid {fmtMoney(p.paid)}</span>
                              <span className="text-red-600 font-medium">{fmtMoney(p.balance)}</span>
                            </div>
                            <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                              <div className="h-full bg-emerald-500"
                                style={{ width: `${Math.min(100, Math.max(0, (p.paid / Math.max(1, p.total_amount)) * 100))}%` }} />
                            </div>
                          </div>
                          {/* Right: due date + inline edit */}
                          <div className="min-w-[170px] flex items-center gap-1 justify-end">
                            {isEditing ? (
                              <>
                                <Input type="date" className="h-7 text-xs w-[130px]" value={editDate}
                                  onChange={(e) => setEditDate(e.target.value)}
                                  data-testid={`due-date-input-${k}`} />
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-emerald-600"
                                  onClick={() => saveDueDate(p)} data-testid={`due-date-save-${k}`}><Check className="w-3.5 h-3.5" /></Button>
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive"
                                  onClick={() => setEditingKey(null)}><X className="w-3.5 h-3.5" /></Button>
                              </>
                            ) : (
                              <>
                                <Badge variant="outline" className={`text-[10px] ${urgencyPill(p.urgency)}`}>
                                  {urgencyLabel(p.urgency, p.days_until_due)}
                                </Badge>
                                <span className="text-[11px] text-muted-foreground tabular-nums">
                                  {p.due_date ? formatDate(p.due_date) : '—'}
                                </span>
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                                  onClick={() => { setEditingKey(k); setEditDate(p.due_date ? p.due_date.slice(0, 10) : ''); }}
                                  title="Edit due date"
                                  data-testid={`due-date-edit-${k}`}>
                                  <Pencil className="w-3.5 h-3.5" />
                                </Button>
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-emerald-700 hover:bg-emerald-50"
                                  onClick={(e) => { e.stopPropagation(); startAddPay(p); }}
                                  title="Add payment"
                                  data-testid={`payable-add-pay-${k}`}>
                                  <Plus className="w-3.5 h-3.5" />
                                </Button>
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                                  onClick={(e) => { e.stopPropagation(); togglePayments(p); }}
                                  title="Show payments"
                                  data-testid={`payable-show-pay-${k}`}>
                                  {showPayments === k ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                </Button>
                              </>
                            )}
                          </div>
                          {/* Inline add-payment form */}
                          {payRow === k && (
                            <div className="basis-full flex flex-wrap items-end gap-2 pt-2 mt-1 border-t" data-testid={`payable-add-form-${k}`}>
                              <div className="flex-1 min-w-[120px]">
                                <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Amount</label>
                                <Input type="number" min="1" value={payAmount} onChange={(e) => setPayAmount(e.target.value)}
                                  className="h-8 text-sm" data-testid={`payable-pay-amount-${k}`} />
                              </div>
                              <div>
                                <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Date</label>
                                <Input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)}
                                  className="h-8 text-sm w-[140px]" data-testid={`payable-pay-date-${k}`} />
                              </div>
                              <div className="flex-1 min-w-[140px]">
                                <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Notes</label>
                                <Input value={payNotes} onChange={(e) => setPayNotes(e.target.value)}
                                  placeholder="UTR / mode…" className="h-8 text-sm"
                                  data-testid={`payable-pay-notes-${k}`} />
                              </div>
                              <Button size="sm" className="h-8" disabled={savingPay}
                                onClick={() => submitPay(p)} data-testid={`payable-pay-submit-${k}`}>
                                {savingPay ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Record'}
                              </Button>
                              <Button size="sm" variant="ghost" className="h-8" onClick={() => setPayRow(null)}>Cancel</Button>
                            </div>
                          )}
                          {/* Expanded existing-payments list */}
                          {showPayments === k && (
                            <div className="basis-full pt-2 mt-1 border-t" data-testid={`payable-payments-${k}`}>
                              {loadingPayments && <div className="text-xs text-muted-foreground py-2">Loading payments…</div>}
                              {!loadingPayments && paymentsList.filter(x => x.tour_id === p.tour_id && (x.vendor_id ? x.vendor_id === p.vendor_id : true) && x.category === p.category).length === 0 && (
                                <div className="text-[11px] text-muted-foreground py-2">No payments recorded yet against this row.</div>
                              )}
                              {!loadingPayments && paymentsList
                                .filter(x => x.tour_id === p.tour_id && (x.vendor_id ? x.vendor_id === p.vendor_id : true) && x.category === p.category)
                                .map(x => (
                                  <div key={x.id} className="flex items-center justify-between text-xs py-1">
                                    <span className="text-muted-foreground tabular-nums">{(x.date || x.created_at || '').slice(0, 10)}</span>
                                    <span className="flex-1 px-2 truncate">{x.notes || '—'}</span>
                                    <span className="font-semibold tabular-nums text-emerald-700">{fmtMoney(x.amount)}</span>
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const Field = ({ label, value, onChange, type = 'text', required, testId }) => (
  <div>
    <Label>{label}</Label>
    <Input
      type={type === 'email' ? 'text' : type}
      inputMode={type === 'email' ? 'email' : undefined}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      required={required}
      data-testid={testId}
      step={type === 'number' ? '0.01' : undefined}
    />
  </div>
);

const KPICard = ({ icon: Icon, label, value, color, sub }) => (
  <Card>
    <CardContent className="pt-4 pb-3 px-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg bg-muted ${color}`}><Icon className="w-4 h-4" /></div>
        <div>
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="text-xl font-bold">{value}</p>
          {sub && <p className="text-[10px] text-muted-foreground leading-tight">{sub}</p>}
        </div>
      </div>
    </CardContent>
  </Card>
);

const LeaderboardCard = ({ title, items, metric, emptyHint, onClick, testId }) => {
  const rankIcons = [Crown, Trophy, Medal];
  const rankColors = ['text-amber-500', 'text-slate-400', 'text-orange-600'];
  return (
    <Card data-testid={testId}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center justify-between">
          <span>{title}</span>
          {items.length > 0 && <span className="text-xs font-normal text-muted-foreground">{items.length} total</span>}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="text-xs text-muted-foreground py-6 text-center">{emptyHint}</p>
        ) : (
          <div className="space-y-1 max-h-[360px] overflow-y-auto pr-1">
            {items.map((it, i) => {
              const Icon = rankIcons[i];
              const amt = metric === 'profit' ? it.profit : it.revenue;
              return (
                <div key={it.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => onClick(it.id)} data-testid={`${testId}-row-${i}`}>
                  <div className={`flex items-center justify-center w-7 h-7 rounded-full bg-muted shrink-0 ${rankColors[i] || 'text-muted-foreground'}`}>
                    {Icon ? <Icon className="w-3.5 h-3.5" /> : <span className="text-xs font-semibold">{i + 1}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{it.name}</p>
                    <div className="flex gap-2 text-[10px] text-muted-foreground">
                      <span>{it.referred_count} referred</span>
                      <span>·</span>
                      <span>{it.tours} tour{it.tours !== 1 ? 's' : ''}</span>
                      {it.role && <><span>·</span><span className="truncate">{it.role}</span></>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-bold">{(amt || 0).toLocaleString('en-IN')}</p>
                    <p className="text-[10px] text-muted-foreground">{metric === 'profit' ? 'Profit' : 'Revenue'}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const CalendarGrid = ({ month, events, selectedDate, onDateClick, onPrev, onNext, large, small }) => {
  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startDay = getDay(monthStart);
  const blanks = Array(startDay).fill(null);
  const allCells = [...blanks, ...days];

  return (
    <Card data-testid={large ? 'main-calendar' : 'next-month-calendar'}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          {onPrev && <Button variant="ghost" size="sm" onClick={onPrev}><ChevronLeft className="w-4 h-4" /></Button>}
          <CardTitle className={small ? 'text-sm' : 'text-base'}>{format(month, 'MMMM yyyy')}</CardTitle>
          {onNext && <Button variant="ghost" size="sm" onClick={onNext}><ChevronRight className="w-4 h-4" /></Button>}
        </div>
        {large && (
          <div className="flex gap-3 mt-2 flex-wrap">
            <span className="flex items-center gap-1 text-[10px] text-muted-foreground"><span className="w-2 h-2 rounded-full bg-emerald-500" />Check-in</span>
            <span className="flex items-center gap-1 text-[10px] text-muted-foreground"><span className="w-2 h-2 rounded-full bg-red-500" />Check-out</span>
            <span className="flex items-center gap-1 text-[10px] text-muted-foreground"><span className="w-2 h-2 rounded-full bg-blue-500" />Activity</span>
            <span className="flex items-center gap-1 text-[10px] text-muted-foreground"><span className="w-2 h-2 rounded-full bg-amber-500" />Transport</span>
          </div>
        )}
      </CardHeader>
      <CardContent className="px-2 pb-2">
        <div className="grid grid-cols-7 text-center mb-1">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
            <div key={d} className={`${small ? 'text-[10px]' : 'text-xs'} font-medium text-muted-foreground py-1`}>{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {allCells.map((day, idx) => {
            if (!day) return <div key={`b-${idx}`} />;
            const dateStr = format(day, 'yyyy-MM-dd');
            const dayEvents = events[dateStr] || [];
            const isSelected = selectedDate === dateStr;
            const isToday = isSameDay(day, new Date());
            const uniqueTypes = [...new Set(dayEvents.map(e => e.type))];
            return (
              <button key={dateStr} onClick={() => onDateClick?.(dateStr)}
                className={`relative ${small ? 'p-0.5' : 'p-1'} rounded-lg transition-colors text-center
                  ${isSelected ? 'bg-primary text-white' : isToday ? 'bg-primary/10' : 'hover:bg-muted'}`}
                data-testid={`cal-${dateStr}`}>
                <span className={`${small ? 'text-[10px]' : 'text-sm'} ${isSelected ? 'font-bold' : ''}`}>{format(day, 'd')}</span>
                {uniqueTypes.length > 0 && (
                  <div className="flex justify-center gap-0.5 mt-0.5">
                    {uniqueTypes.slice(0, 4).map(t => <span key={t} className={`w-1.5 h-1.5 rounded-full ${eventColors[t]}`} />)}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default PollenDotsDashboard;
